import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ActivityIndicator, RefreshControl, StyleSheet, View, ViewToken} from 'react-native';
import {LayoutAnimationConfig} from 'react-native-reanimated';
import {useHeaderHeight} from '@react-navigation/elements';
import {FlashList, FlashListRef} from '@shopify/flash-list';
import {useQueryClient} from '@tanstack/react-query';
import {runInAction} from 'mobx';
import {observer} from 'mobx-react-lite';

import DiscoverTile from '../../components/articleTile/DiscoverTile';
import DiscoverFilters from '../../components/DiscoverFilters';
import RightSideFilters from '../../components/RightSideFilters';
import {DiscoverTileSkeleton} from '../../components/skeleton';
import ContentView from '../../components/ui-controls/layout/ContentView';
import {
  FOOTER_PARENT_STYLE,
  SiteFooter,
  usePageGutter,
} from '../../components/WebFooter/SiteFooter';
import {useOptionalScrollCollapseContext} from '../../context/ScrollCollapseContext';
import {
  useScrollCollapseExpandWhile,
  useScrollCollapseJSHandler,
} from '../../hooks/useScrollCollapse';
import {useTabBarBottomPadding} from '../../hooks/useTabBarBottomPadding';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AirtableFeedItem} from '../../models/airtable';
import {ALL_SEGMENT_VALUE, useFeedDiscoverList} from '../../queries/feed/useFeedDiscoverList';
import {useFeedSegments} from '../../queries/feed/useFeedSegments';
import {useStores} from '../../stores/use-stores';
import {distributeVideos} from '../../utils/feedDistribute';

// DIS-1444: Discover is a forward-paginating feed, not a chat. FlashList v2
// enables maintainVisibleContentPosition by default, which anchors the scroll
// to a visible item when content changes — that is exactly what kept the stale
// offset on a segment switch (the original bug), and it has no valid anchor when
// a segment/store switch replaces the whole dataset, so it fights the
// scrollToOffset(0) reset and leaves the list blank until a manual scroll
// re-anchors. Disable it so the reset repaints from the top. Stable module
// const so the prop identity doesn't churn per render.
const MAINTAIN_VISIBLE_CONTENT_POSITION = {disabled: true};

/**
 * DiscoverListScreen - server-paginated, per-segment discover feed.
 *
 * Backed by `useFeedDiscoverList` (useInfiniteQuery, offset/limit) keyed on
 * `feedStore.selectedSegment.value`. Segment changes reactively update the
 * query key — switching tabs refetches that segment's first page.
 *
 * Wrapped in `observer()` so MobX changes to `feedStore.selectedSegment`
 * trigger a re-render that re-reads the segment prop value and feeds it
 * into the hook.
 */
const DiscoverListScreen = observer(() => {
  const {feedStore, shopSelectionStore} = useStores();
  const {isWebDesktopScreen} = useWebScreenDimensions();
  // FlashList v2 puts `style` on an overflow-hidden wrapper around its ScrollView, so the
  // usePageBleed() padding there kept the ScrollView in the content column and it clipped the
  // footer (DIS-1946). Widen the list by the gutter and pad its content instead.
  const gutter = usePageGutter();
  const tabBarPadding = useTabBarBottomPadding({skipFocusReset: true});
  const listRef = useRef<FlashListRef<AirtableFeedItem>>(null);
  const {scrollHandler} = useScrollCollapseJSHandler(listRef);
  const headerHeight = useHeaderHeight();
  const queryClient = useQueryClient();
  const scrollCollapseCtx = useOptionalScrollCollapseContext();

  const selectedStore = shopSelectionStore?.selectedStore;
  const storeNumber = selectedStore?.store_number;
  const storeState = selectedStore?.address?.administrative_area?.toUpperCase();
  const segment = feedStore.selectedSegment.value;

  const {data, isPending, isFetchingNextPage, hasNextPage, fetchNextPage} = useFeedDiscoverList({
    storeNumber,
    storeState,
    segment,
  });
  const {data: segmentsData} = useFeedSegments({storeNumber, storeState});

  // Render directly from TanStack pages. After DIS-1344, Discover owns its
  // list via this infinite query — feedStore.discoverList is not written here
  // and must not be treated as the Discover source of truth (DIS-1516: Detail
  // resolves stubs from this cache, not MobX). Bridging pages into MobX would
  // also reintroduce the DIS-1306 Home/Discover race.
  //
  // Segment filter runs client-side defensively even though queryKey carries
  // `segment` (server uses it as a hint, not a guarantee). Matches the
  // pre-DIS-1306 feedStore.filteredDiscoverList contract so a segment switch
  // narrows the visible list immediately regardless of backend behaviour.
  const discoveryList = useMemo<AirtableFeedItem[]>(() => {
    if (!data?.pages) return [];
    const seen = new Set<string>();
    const flat: AirtableFeedItem[] = [];
    for (const page of data.pages) {
      for (const item of page.discoverList) {
        if (seen.has(item.id)) continue;
        seen.add(item.id);
        flat.push(item);
      }
    }
    const filtered =
      segment === ALL_SEGMENT_VALUE ? flat : flat.filter(item => item.segment === segment);
    return distributeVideos(filtered);
  }, [data, segment]);

  // The skeleton branch renders no FlashList, so nothing drives the shared
  // scroll-collapse header back to its expanded state. Pin it expanded while
  // the skeleton shows, otherwise a stale ctx.scrollY leaves the transparent
  // header collapsed off-screen (blank header gap during loading).
  const showingSkeleton = isPending && discoveryList.length === 0;
  useScrollCollapseExpandWhile(showingSkeleton);

  // DIS-1444: reset scroll to top + re-expand the collapsing header when the
  // visible feed changes — either the selected segment OR the store. The
  // FlashList is intentionally NOT remounted (no key) so its v2 recycling pool
  // survives — keeping the DIS-1338 Android-OOM hardening intact — but that
  // means it otherwise retains the previous scroll offset. The query key is
  // ['feed','discover','v3', storeNumber, storeState, segment] under
  // placeholderData: keepPreviousData, so a segment OR store switch swaps the
  // list data in place without a remount, leaving the new list's top rows above
  // the fold. Keying only on `segment` would miss a store switch on the ALL
  // segment. Mirrors RangeProductListScreen's filter-change scroll reset. Skips
  // the first render so the initial mount position is respected.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    listRef.current?.scrollToOffset({offset: 0, animated: false});
    if (scrollCollapseCtx) {
      scrollCollapseCtx.scrollY.set(0);
      scrollCollapseCtx.prevScrollY.set(0);
      scrollCollapseCtx.accumulatedUp.set(scrollCollapseCtx.TAB_BAR_HEIGHT);
      scrollCollapseCtx.accumulatedDown.set(0);
    }
  }, [segment, storeNumber, storeState, scrollCollapseCtx]);

  useEffect(() => {
    if (segmentsData === undefined) return;
    runInAction(() => {
      feedStore.setServerSegments(segmentsData);
    });
  }, [segmentsData, feedStore]);

  const numberOfColumns = isWebDesktopScreen ? 4 : 2;

  const contentStyle = useMemo(
    () => ({...styles.contentContainer, paddingTop: headerHeight, paddingBottom: tabBarPadding}),
    [headerHeight, tabBarPadding],
  );

  const [isRefreshing, setIsRefreshing] = useState(false);
  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        queryClient.invalidateQueries({queryKey: ['feed', 'discover']}),
        queryClient.invalidateQueries({queryKey: ['segments', 'feed']}),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  }, [queryClient]);

  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Single-active video player: track the most-visible video cell so only it
  // mounts a real <Video> (frees MediaCodec decoders + caps Android heap on scroll).
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);

  // Stable across renders — a fresh viewabilityConfig object would recreate
  // FlashList's ViewabilityManager and lose tracking. itemVisiblePercentThreshold
  // (not the area variant) is the reliable choice for masonry/multi-column.
  const viewabilityConfig = useRef({
    itemVisiblePercentThreshold: 60,
    minimumViewTime: 300,
  }).current;

  // Must be stable (useCallback []) for the same reason. Picks the topmost viewable
  // video item; when none is viewable, no video plays.
  const onViewableItemsChanged = useCallback(({viewableItems}: {viewableItems: ViewToken[]}) => {
    const firstVideo = viewableItems.find(
      token => (token.item as AirtableFeedItem)?.heroImage?.type === 'video',
    );
    setActiveVideoId(firstVideo ? (firstVideo.item as AirtableFeedItem).id : null);
  }, []);

  const renderItem = useCallback(
    ({item}: {item: AirtableFeedItem}) => (
      <View style={styles.tileWrapper}>
        <DiscoverTile item={item} isActive={item.id === activeVideoId} />
      </View>
    ),
    [activeVideoId],
  );

  const keyExtractor = useCallback((item: AirtableFeedItem) => item.id, []);

  const ListFooterComponent = useCallback(() => {
    if (isFetchingNextPage) {
      return (
        <View style={styles.footerSpinner} testID="discover-footer-spinner">
          <ActivityIndicator />
        </View>
      );
    }
    return (
      <View style={styles.footerBleed}>
        <SiteFooter />
      </View>
    );
  }, [isFetchingNextPage]);

  const skeletonHeights = useMemo(() => [180, 140, 220, 160, 200, 130, 240, 150], []);
  const skeletonRowsPerCol = 3;
  const skeletonCellsPerCol = isWebDesktopScreen ? skeletonRowsPerCol : skeletonRowsPerCol + 1;

  const renderSkeletonGrid = () => (
    <View style={[styles.skeletonGrid, contentStyle]} testID="discover-skeleton-grid">
      {Array.from({length: numberOfColumns}).map((_, col) => (
        <View key={`skeleton-col-${col}`} style={styles.skeletonColumn}>
          {Array.from({length: skeletonCellsPerCol}).map((__, row) => {
            const i = col * skeletonCellsPerCol + row;
            return (
              <DiscoverTileSkeleton
                key={`skeleton-${col}-${row}`}
                index={i}
                imageHeight={skeletonHeights[i % skeletonHeights.length]}
              />
            );
          })}
        </View>
      ))}
    </View>
  );

  if (showingSkeleton) {
    return (
      <RightSideFilters menuWidthProportion={0.7} menu={<DiscoverFilters />}>
        <ContentView testID="discover-screen">{renderSkeletonGrid()}</ContentView>
      </RightSideFilters>
    );
  }

  return (
    <RightSideFilters menuWidthProportion={0.7} menu={<DiscoverFilters />}>
      <ContentView testID="discover-screen" parentStyle={FOOTER_PARENT_STYLE}>
        <LayoutAnimationConfig skipEntering>
          <FlashList
            style={gutter ? {marginHorizontal: -gutter} : undefined}
            ref={listRef}
            data={discoveryList}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            numColumns={numberOfColumns}
            masonry
            maintainVisibleContentPosition={MAINTAIN_VISIBLE_CONTENT_POSITION}
            estimatedItemSize={250}
            getItemType={item => (item.heroImage?.type === 'video' ? 'video' : 'image')}
            viewabilityConfig={viewabilityConfig}
            onViewableItemsChanged={onViewableItemsChanged}
            extraData={activeVideoId}
            refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={refresh} />}
            showsVerticalScrollIndicator={isWebDesktopScreen}
            showsHorizontalScrollIndicator={false}
            onScroll={scrollHandler}
            scrollEventThrottle={16}
            onEndReached={handleEndReached}
            onEndReachedThreshold={0.5}
            ListFooterComponent={ListFooterComponent}
            contentContainerStyle={[
              contentStyle,
              !!gutter && {paddingHorizontal: CONTENT_PADDING_X + gutter},
            ]}
            testID="discover-list"
          />
        </LayoutAnimationConfig>
      </ContentView>
    </RightSideFilters>
  );
});

export default DiscoverListScreen;

const CONTENT_PADDING_X = 8;

const styles = StyleSheet.create({
  contentContainer: {
    paddingHorizontal: CONTENT_PADDING_X,
  },
  // The footer spans the window (SiteFooter); undo the masonry's own side padding for it.
  footerBleed: {
    marginHorizontal: -CONTENT_PADDING_X,
  },
  tileWrapper: {
    flex: 1,
    paddingHorizontal: 4,
  },
  footerSpinner: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  skeletonGrid: {
    flexDirection: 'row',
    width: '100%',
    alignItems: 'flex-start',
  },
  skeletonColumn: {
    flex: 1,
    paddingHorizontal: 4,
  },
});
